import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Camera, Check, ChevronDown, Eye, ImagePlus, Pencil, Plus, Save, Trash2, X } from "lucide-react";
import PortraitCropEditor from "@/components/account/PortraitCropEditor";
import { PROFESSIONAL_ROLE_GROUPS } from "@/data/professionalMapData";
import ProfessionalMapInbox from "@/components/professional-map/ProfessionalMapInbox";
import ProfessionalMapOffersWorkspace from "@/components/professional-map/ProfessionalMapOffersWorkspace";
import ProfessionalProfileView from "@/components/professional-map/ProfessionalProfileView";
import ProfessionalMapContactDialog from "@/components/professional-map/ProfessionalMapContactDialog";
import ProfessionalMapGeographyFields from "@/components/professional-map/ProfessionalMapGeographyFields";

const BLANK = {
  entity_type: "person", business_type: "", public_name: "", description: "", professional_qa: [],
  specializations: "", services: "", country: "", region: "", city: "",
  remote: false, delivery_area: "", public_email: "", public_phone: "",
  public_contact_email_visible: false, public_contact_phone_visible: false,
  public_website: "", accepting_orders: "",
  portrait_crop_x: 50, portrait_crop_y: 50, social_links: [],
};
const BUSINESS_TYPES = [
  ["cafe", "Кафе"], ["coffee_shop", "Кофейня"],
  ["pastry_shop", "Кондитерская"], ["bakery", "Пекарня"],
  ["dessert_studio", "Десертная студия"],
  ["supplier", "Поставщик ингредиентов, упаковки или оборудования"],
  ["production", "Цех или производство"],
  ["services", "Профессиональные услуги"],
  ["shop", "Магазин кондитерских товаров"], ["other", "Другое предприятие"],
];
const businessLabel = (value) => BUSINESS_TYPES.find(([id]) => id === value)?.[1] || "";
const SOCIAL_TYPES = [["instagram","Instagram"],["telegram","Telegram"],["vk","ВКонтакте"],["max","MAX"],["facebook","Facebook"],["tiktok","TikTok"],["youtube","YouTube"],["threads","Threads"],["pinterest","Pinterest"],["website","Сайт"],["other","Другое"]];
const socialLabel = (v) => SOCIAL_TYPES.find(([id]) => id === v)?.[1] || v;
const safeSocialUrl = (value) => {
  try {
    const u = new URL(value);
    const host = u.hostname.toLowerCase();
    if (u.protocol !== "https:" || u.username || u.password || !host ||
        host === "localhost" || host.endsWith(".local") || host.endsWith(".internal") ||
        /^\d+\.\d+\.\d+\.\d+$/.test(host) || host.includes(":")) return false;
    return true;
  } catch { return false; }
};

const field = "w-full rounded-xl border border-[#826751]/25 bg-white/80 px-3 py-3 text-sm text-[#3B2C22] outline-none focus:border-[#826751] focus-visible:ring-2 focus-visible:ring-[#826751]/20";
const softButton = "inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-[#826751]/25 bg-[#FFFCF8] px-4 py-2 text-sm text-[#4A382B] transition-colors hover:bg-[#F3E8DA] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#755F4E]";

function commaList(value) {
  return [...new Set(String(value || "").split(",").map((item) => item.trim()).filter(Boolean))];
}
function editable(card) {
  return {
    ...BLANK, ...card,
    business_type: card.business_type || "",
    social_links: (card.social_links || []).map((link) => ({ ...link })),
    specializations: (card.specializations || []).join(", "),
    services: (card.services || []).join(", "),
    professional_qa: (card.professional_qa || []).map(({ question, answer }) => ({ question, answer })),
    public_email: card.public_email || "",
    public_website: card.public_website || "",
    accepting_orders: card.accepting_orders == null ? "" : (card.accepting_orders ? "yes" : "no"),
  };
}
function payload(form) {
  return {
    entity_type: form.entity_type,
    ...(form.entity_type === "business" ? { business_type: form.business_type || null } : {}),
    public_name: form.public_name.trim(),
    description: form.description.trim(),
    specializations: commaList(form.specializations),
    services: commaList(form.services),
    professional_qa: (form.professional_qa || []).map(({ question, answer }) => ({ question: question.trim(), answer: answer.trim() })),
    country: form.country.trim(),
    region: form.region.trim(),
    city: form.city.trim(),
    remote: Boolean(form.remote),
    delivery_area: form.delivery_area.trim(),
    public_email: form.public_email.trim() || null,
    public_phone: form.public_phone.trim(),
    public_contact_email_visible: form.public_contact_email_visible === true && Boolean(form.public_email.trim()),
    public_contact_phone_visible: form.public_contact_phone_visible === true && Boolean(form.public_phone.trim()),
    public_website: form.public_website.trim() || null,
    social_links: form.social_links.filter((link) => link.url.trim()).map((link) => ({ platform: link.platform, url: link.url.trim(), visible: link.visible !== false })),
    accepting_orders: form.accepting_orders === "" ? null : form.accepting_orders === "yes",
    portrait_crop_x: Number(form.portrait_crop_x), portrait_crop_y: Number(form.portrait_crop_y),
  };
}

// Never expose private media URLs directly. The browser requests each image with the owner's bearer token.
function OwnedImage({ mediaId, aspect = "aspect-[4/5]", onRemove, onEdit, onOpen, captionOverride, position, portrait = false, showCaption = true }) {
  const [photo, setPhoto] = useState({ src: "", caption: "" });
  useEffect(() => {
    let live = true;
    let blobUrl = null;
    (async () => {
      try {
        const { data } = await api.get("/account/media/" + encodeURIComponent(mediaId));
        const path = data.file_url?.replace(/^\/api\//, "/");
        if (!path?.startsWith("/media/files/")) throw new Error("Фотография недоступна");
        const response = await api.get(path, { responseType: "blob" });
        blobUrl = URL.createObjectURL(response.data);
        if (live) setPhoto({ src: blobUrl, caption: data.title || "" });
      } catch (_err) {
        if (live) setPhoto({ src: "", caption: "Не удалось загрузить изображение" });
      }
    })();
    return () => {
      live = false;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [mediaId]);
  const caption = typeof captionOverride === "string" ? captionOverride : photo.caption;
  return (
    <figure className="min-w-0" data-testid={portrait ? "pm-profile-portrait-image" : "professional-portfolio-image"}>
      <div className={"relative overflow-hidden rounded-2xl border border-[#826751]/20 bg-[#F2E9DC] " + aspect}>
        {photo.src ? (onOpen && !portrait ?
            <button type="button" className="h-full w-full cursor-zoom-in" onClick={() => onOpen({ src: photo.src, alt: caption || "Работа" })}
              aria-label={"Увеличить фотографию " + (caption || "работы")}>
              <img src={photo.src} alt={caption || "Работа"} className="h-full w-full object-cover" />
            </button> :
            <img src={photo.src} alt={photo.caption || (portrait ? "Портрет" : "Работа")} className="h-full w-full object-cover" style={portrait ? {objectPosition: (position?.x ?? 50) + "% " + (position?.y ?? 50) + "%"} : undefined} />) :
          <div className="flex h-full items-center justify-center p-2 text-center text-xs text-[#806B5C]">{photo.caption || "Фотография"}</div>}
        {onEdit && photo.src && <button type="button" onClick={() => onEdit(photo.src)} className="absolute bottom-2 left-2 flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 shadow-sm" aria-label="Редактировать положение портрета"><Pencil size={16}/></button>}
        {onRemove && <button type="button" onClick={onRemove} className="absolute right-2 top-2 flex h-10 w-10 items-center justify-center rounded-xl bg-white/95 shadow-sm" aria-label={portrait ? "Убрать портрет" : "Убрать работу"}>
          <Trash2 size={16} aria-hidden="true" /></button>}
      </div>
      {showCaption && !portrait && caption && <figcaption className="mt-2 break-words text-xs text-[#665344]">{caption}</figcaption>}
    </figure>
  );
}
function Section({ title, hint, open, onToggle, children, testId, hideToggle = false, joined = false }) {
  return (
    <section className={joined ? "border-t border-[#826751]/15 px-4 pb-3 pt-3 sm:px-7 sm:pb-4 sm:pt-3" : "rounded-[24px] border border-[#826751]/15 bg-white/65 p-4 sm:p-6"} data-testid={testId}>
      {!hideToggle ? <button type="button" onClick={onToggle} aria-expanded={open}
        className="flex min-h-[48px] w-full items-center justify-between gap-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#755F4E]">
        <span><span className="block font-serif text-xl sm:text-2xl">{title}</span>
          {hint && <span className="mt-1 block text-xs text-[#806B5C]">{hint}</span>}</span>
        {open ? <ChevronDown className="h-5 w-5 rotate-180" /> : <Plus className="h-5 w-5" />}
      </button> : <h2 className="font-serif text-xl sm:text-2xl">{title}</h2>}
      {open && <div className={joined ? "mt-3 space-y-4" : "mt-5 space-y-4 border-t border-[#826751]/15 pt-5"}>{children}</div>}
    </section>
  );
}

export default function ProfessionalCardWorkspace() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedCardId = searchParams.get("card");
  const fromAccount = searchParams.get("from") === "account";
  const returnToOrigin = fromAccount
    ? (searchParams.get("return") === "profile" ? "/account?tab=profile" : "/account")
    : "/professional-map";
  const portraitRef = useRef(null);
  const worksRef = useRef(null);
  const quickWorkRef = useRef(null);
  const stagedRef = useRef({ portrait: null, works: [] });
  const [cards, setCards] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ ...BLANK });
  const [portraitRemoved, setPortraitRemoved] = useState(false);
  const [removedWorks, setRemovedWorks] = useState([]);
  const [pendingPortrait, setPendingPortrait] = useState(null);
  const [pendingWorks, setPendingWorks] = useState([]);
  const [preview, setPreview] = useState(false);
  const [previewContactMode, setPreviewContactMode] = useState(null);
  const [offerEditorOpen, setOfferEditorOpen] = useState(false);
  const [opened, setOpened] = useState({ about: true, works: true });
  const [canOwnerQa, setCanOwnerQa] = useState(false);
  const [canStaffSelfPublish, setCanStaffSelfPublish] = useState(false);
  const [publicationConsent, setPublicationConsent] = useState(false);
  const [staffPublicationBusy, setStaffPublicationBusy] = useState(false);
  const [promoInvitation, setPromoInvitation] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [quickUploading, setQuickUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [portfolioEditing, setPortfolioEditing] = useState(false);
  const [portfolioOrder, setPortfolioOrder] = useState([]);
  const [portfolioCaptions, setPortfolioCaptions] = useState({});
  const [portfolioSaving, setPortfolioSaving] = useState(false);
  const [showRoleChoices, setShowRoleChoices] = useState(false);
  const [cropSource, setCropSource] = useState(null);
  const [showContacts, setShowContacts] = useState(false);
  const [showSocialChoice, setShowSocialChoice] = useState(false);
  const [showBusinessChoice, setShowBusinessChoice] = useState(false);

  useEffect(() => {
    let active = true;
    api.get("/auth/me").then(({ data }) => {
      if (active) {
        setCanOwnerQa(data?.role === "owner");
        setCanStaffSelfPublish(data?.role === "owner" || data?.role === "administrator");
      }
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let live = true;
    api.get("/professional-map/complimentary/mine")
      .then(({ data }) => { if (live) setPromoInvitation(data || null); })
      .catch(() => { if (live) setPromoInvitation(null); });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    let live = true;
    api.get("/professional-map/cards")
      .then(({ data }) => {
        if (!live) return;
        const saved = data || [];
        setCards(saved);
        if (saved.length > 0) {
          const selected = saved.find((card) => card.id === requestedCardId) || saved[0];
          setActive(selected);
          setForm(editable(selected));
          setPortfolioCaptions({ ...(selected.portfolio_captions || {}) });
          setPreview(true);
        }
      })
      .catch((error) => {
        if (live) { setLoadError(true); toast.error(formatApiError(error)); }
      })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [requestedCardId]);

  useEffect(() => { stagedRef.current = { portrait: pendingPortrait, works: pendingWorks }; }, [pendingPortrait, pendingWorks]);
  useEffect(() => () => {
    const items = stagedRef.current;
    if (items.portrait) URL.revokeObjectURL(items.portrait.url);
    items.works.forEach((item) => URL.revokeObjectURL(item.url));
  }, []);

  function set(name, value) { setForm((old) => ({ ...old, [name]: value })); }
  function toggle(name) { setOpened((old) => ({ ...old, [name]: !old[name] })); }
  function removeTemp(item, portrait = false) {
    URL.revokeObjectURL(item.url);
    if (portrait) setPendingPortrait(null);
    else setPendingWorks((old) => old.filter((entry) => entry !== item));
  }
  function resetStaged() {
    if (pendingPortrait) URL.revokeObjectURL(pendingPortrait.url);
    pendingWorks.forEach((item) => URL.revokeObjectURL(item.url));
    setPendingPortrait(null);
    setPendingWorks([]);
    setPortraitRemoved(false);
    setRemovedWorks([]);
  }
  function selectPortrait(event) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return toast.error("Выбери JPG, PNG или WebP");
    if (pendingPortrait) URL.revokeObjectURL(pendingPortrait.url);
    const url = URL.createObjectURL(file);
    setPendingPortrait({ file, url });
    set("portrait_crop_x", 50);
    set("portrait_crop_y", 50);
    setCropSource(url);
    setPortraitRemoved(false);
  }
  function selectWorks(event) {
    const files = [...(event.target.files || [])];
    event.target.value = "";
    const allowed = files.filter((file) => ["image/jpeg", "image/png", "image/webp"].includes(file.type));
    // The gallery has no 20-image product cap. Keep batches small for mobile
    // upload reliability and avoid unbounded concurrent media transfers.
    if (allowed.length > 12) {
      toast.error("Добавляй до 12 фотографий за один раз.");
      return;
    }
    if (files.length !== allowed.length) toast.error("Поддерживаются JPG, PNG и WebP");
    setPendingWorks((old) => [...old, ...allowed.map((file) => ({
      file, url: URL.createObjectURL(file), caption: file.name.replace(/\.[^.]+$/, "").slice(0, 200),
    }))]);
  }
  const hasUnsavedDraft = Boolean(
    portfolioEditing || pendingPortrait || pendingWorks.length || portraitRemoved || removedWorks.length ||
    (active && JSON.stringify(portfolioCaptions) !== JSON.stringify(active.portfolio_captions || {})) ||
    (active ? JSON.stringify(payload(form)) !== JSON.stringify(payload(editable(active))) :
      form.public_name.trim() || form.description.trim() || commaList(form.specializations).length ||
      commaList(form.services).length || form.country.trim() || form.city.trim() ||
      form.business_type || form.public_email.trim() || form.public_phone.trim() ||
      (form.professional_qa || []).some(({ question, answer }) => question.trim() || answer.trim()) ||
      (form.social_links || []).length || form.public_website.trim())
  );
  function returnToProfile() {
    if (saving || portfolioSaving || quickUploading) return;
    if (hasUnsavedDraft || offerEditorOpen) {
      if (!window.confirm("Есть несохранённые изменения. Вернуться к странице без сохранения?")) return;
      resetStaged();
      if (active) {
        setForm(editable(active));
        setPortfolioCaptions({ ...(active.portfolio_captions || {}) });
      }
      setPortfolioEditing(false);
      setOfferEditorOpen(false);
    }
    setPreview(true);
  }
  function canLeaveDraft() {
    if (!hasUnsavedDraft && !offerEditorOpen) return true;
    toast.error("Сохрани изменения перед переходом к другой странице.");
    return false;
  }
  // Existing free-form specializations remain intact; the editor offers canon roles
  // without writing a second parallel taxonomy into professional drafts.
  function toggleRole(role) {
    const current = commaList(form.specializations);
    const key = (item) => item.toLocaleLowerCase("ru-RU");
    const selected = current.some((item) => key(item) === key(role));
    if (!selected && current.length >= 20) {
      toast.error("Можно выбрать до 20 специализаций");
      return;
    }
    set("specializations", (selected
      ? current.filter((item) => key(item) !== key(role))
      : [...current, role]).join(", "));
  }
  function switchTo(card) {
    if (!card || !canLeaveDraft()) return;
    resetStaged();
    setActive(card);
    setForm(editable(card));
    setPortfolioCaptions({ ...(card.portfolio_captions || {}) });
    setShowRoleChoices(false);
    setShowBusinessChoice(false);
    setShowSocialChoice(false);
    setPreview(true);
    setPublicationConsent(false);
    setPortfolioEditing(false);
    setOpened({ about: true, works: true });
  }
  function createBusinessDraft() {
    if (!canLeaveDraft()) return;
    resetStaged();
    setActive(null);
    setForm({ ...BLANK, entity_type: "business" });
    setPortfolioCaptions({});
    setShowRoleChoices(false);
    setShowBusinessChoice(false);
    setShowSocialChoice(false);
    setPreview(false);
    setPortfolioEditing(false);
    setOpened({ about: true, works: true });
  }
  async function uploadOne(file, title, created) {
    const body = new FormData();
    body.append("file", file);
    body.append("title", title || file.name);
    const { data } = await api.post("/account/media/upload-file", body);
    created.push(data.id);
    return data.id;
  }
  function beginPortfolioEditing() {
    if (!active || preview || saving || quickUploading) return;
    if (hasUnsavedDraft) {
      toast.error("Сначала сохрани изменения страницы.");
      return;
    }
    setPortfolioOrder([...(active.portfolio_media_ids || [])]);
    setPortfolioCaptions({ ...(active.portfolio_captions || {}) });
    setPortfolioEditing(true);
  }

  function moveWork(mediaId, offset) {
    setPortfolioOrder((before) => {
      const next = [...before];
      const from = next.indexOf(mediaId);
      const to = from + offset;
      if (from < 0 || to < 0 || to >= next.length) return before;
      [next[from], next[to]] = [next[to], next[from]];
      return next;
    });
  }

  async function savePortfolioEditing() {
    if (!active || !portfolioEditing || portfolioSaving) return;
    setPortfolioSaving(true);
    try {
      const captions = Object.fromEntries(
        Object.entries(portfolioCaptions)
          .filter(([id]) => portfolioOrder.includes(id))
          .map(([id, value]) => [id, String(value || "").trim().slice(0, 200)])
      );
      const { data } = await api.patch("/professional-map/cards/" + encodeURIComponent(active.id), {
        expected_revision: active.revision,
        portfolio_media_ids: portfolioOrder,
        portfolio_captions: captions,
      });
      setCards((old) => [data, ...old.filter((card) => card.id !== data.id)]);
      setActive(data);
      setForm(editable(data));
      setPortfolioCaptions({ ...(data.portfolio_captions || {}) });
      setPortfolioEditing(false);
      toast.success("Портфолио сохранено");
    } catch (_error) {
      // Keep staged captions and order for retry; never delete the original images.
      toast.error("Не удалось подтвердить сохранение. Обнови страницу для проверки перед повтором.");
    } finally {
      setPortfolioSaving(false);
    }
  }

  async function quickAddWork(event) {
    const selected = event.target.files?.[0];
    event.target.value = "";
    if (!selected || !active || preview || quickUploading || saving || portfolioEditing) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(selected.type)) {
      toast.error("Выбери JPG, PNG или WebP");
      return;
    }
    if (hasUnsavedDraft) {
      toast.error("Сначала сохрани несохранённые изменения.");
      return;
    }
    setQuickUploading(true);
    let didStartCommit = false;
    const created = [];
    try {
      // Reuse private user media. No original is published or copied into a public URL.
      const mediaId = await uploadOne(selected, selected.name.replace(/\.[^.]+$/, "").slice(0, 200), created);
      didStartCommit = true;
      const { data } = await api.patch("/professional-map/cards/" + encodeURIComponent(active.id), {
        expected_revision: active.revision,
        portfolio_media_ids: [...(active.portfolio_media_ids || []), mediaId],
      });
      setCards((old) => [data, ...old.filter((card) => card.id !== data.id)]);
      setActive(data);
      setForm(editable(data));
      setPortfolioCaptions({ ...(data.portfolio_captions || {}) });
      toast.success("Работа добавлена");
    } catch (error) {
      if (!didStartCommit) {
        await Promise.allSettled(created.map((id) => api.delete("/account/media/" + encodeURIComponent(id))));
        toast.error(formatApiError(error));
      } else {
        // An ambiguous PATCH must never delete a file that the server may have saved.
        toast.error("Не удалось подтвердить добавление. Обнови страницу перед повторной попыткой.");
      }
    } finally {
      setQuickUploading(false);
    }
  }

  useEffect(() => {
    if (!lightbox) return undefined;
    const handleKey = (event) => {
      if (event.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [lightbox]);

  async function save(event) {
    event?.preventDefault();
    if (saving) return;
    if (offerEditorOpen) { toast.error("Сначала сохрани предложение или закрой его редактор."); return; }
    const name = form.public_name.trim();
    if (name.length < 2) {
      setOpened((old) => ({ ...old, about: true }));
      toast.error("Добавь имя или название (минимум два символа)");
      return;
    }
    if (form.social_links.some((link) => !link.url.trim() || !safeSocialUrl(link.url.trim()))) {
      setShowContacts(true);
      toast.error("Укажи полную ссылку https:// или удали пустую строку");
      return;
    }
    if ((form.professional_qa || []).some(({ question, answer }) => question.trim().length < 2 || answer.trim().length < 2)) {
      setOpened((old) => ({ ...old, qa: true }));
      toast.error("Для каждого вопроса напиши ответ или удали пустую строку");
      return;
    }
    if (form.entity_type === "business" && !form.business_type) {
      setShowBusinessChoice(true);
      toast.error("Выбери вид бизнеса");
      return;
    }
    setSaving(true);
    const created = [];
    let commitStarted = false;
    try {
      const body = payload(form);
      const originalIds = (active?.portfolio_media_ids || []).filter((id) => !removedWorks.includes(id));
      const uploadedIds = [];
      const captions = { ...(active?.portfolio_captions || {}), ...portfolioCaptions };
      if (pendingPortrait) body.portrait_media_id = await uploadOne(pendingPortrait.file, "Портрет", created);
      else if (portraitRemoved && active) body.portrait_media_id = null;
      for (const work of pendingWorks) {
        const mediaId = await uploadOne(work.file, work.caption.trim() || work.file.name, created);
        uploadedIds.push(mediaId);
        captions[mediaId] = work.caption.trim();
      }
      body.portfolio_media_ids = [...originalIds, ...uploadedIds];
      body.portfolio_captions = Object.fromEntries(
        Object.entries(captions).filter(([id]) => body.portfolio_media_ids.includes(id))
      );
      let data;
      if (active) {
        delete body.entity_type;
        if (form.entity_type !== "business") delete body.business_type;
        commitStarted = true;
        ({ data } = await api.patch("/professional-map/cards/" + encodeURIComponent(active.id), {
          ...body, expected_revision: active.revision,
        }));
      } else {
        commitStarted = true;
        ({ data } = await api.post("/professional-map/cards", {
          ...body, portrait_media_id: body.portrait_media_id || null,
        }));
      }
      resetStaged();
      setCards((old) => [data, ...old.filter((card) => card.id !== data.id)]);
      setActive(data);
      setForm(editable(data));
      setPortfolioCaptions({ ...(data.portfolio_captions || {}) });
      setPreview(true);
      setPortfolioEditing(false);
      toast.success("Страница сохранена");
    } catch (error) {
      // A timeout AFTER POST/PATCH began is ambiguous: the server may already have
      // committed the draft. Deleting uploaded media here could destroy saved work.
      if (!commitStarted) {
        await Promise.allSettled(created.map((id) => api.delete("/account/media/" + encodeURIComponent(id))));
        toast.error(formatApiError(error));
      } else {
        toast.error("Не удалось подтвердить сохранение. Проверь страницу после обновления, прежде чем сохранять повторно.");
      }
    } finally {
      setSaving(false);
    }
  }
  async function staffPublishOwnPage() {
    if (!active || active.status !== "draft" || staffPublicationBusy || !canStaffSelfPublish) return;
    if (!publicationConsent) { toast.error("Подтверди согласие на публикацию страницы."); return; }
    if (hasUnsavedDraft || offerEditorOpen) { toast.error("Сначала сохрани изменения страницы."); return; }
    setStaffPublicationBusy(true);
    try {
      const { data } = await api.post("/professional-map/cards/" + encodeURIComponent(active.id) + "/staff-publish", {
        expected_revision: active.revision, consent_to_publication: true,
      });
      setCards((old) => [data, ...old.filter((card) => card.id !== data.id)]);
      setActive(data);
      setForm(editable(data));
      setPublicationConsent(false);
      setPreview(true);
      toast.success("Профессиональная страница опубликована");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setStaffPublicationBusy(false);
    }
  }
  async function staffWithdrawOwnPage() {
    if (!active || active.status !== "published" || staffPublicationBusy || !canStaffSelfPublish) return;
    if (!window.confirm("Снять страницу с публикации? Она исчезнет из публичного поиска и станет черновиком.")) return;
    setStaffPublicationBusy(true);
    try {
      const { data } = await api.post("/professional-map/cards/" + encodeURIComponent(active.id) + "/staff-withdraw", {
        expected_revision: active.revision,
      });
      setCards((old) => [data, ...old.filter((card) => card.id !== data.id)]);
      setActive(data);
      setForm(editable(data));
      setPublicationConsent(false);
      setPreview(true);
      toast.success("Страница скрыта и доступна для редактирования");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setStaffPublicationBusy(false);
    }
  }
  async function submitPromoPage() {
    if (!active || promoBusy || promoInvitation?.state !== "issued") return;
    if (!publicationConsent) { toast.error("Подтверди согласие на публикацию страницы."); return; }
    if (hasUnsavedDraft || offerEditorOpen) { toast.error("Сначала сохрани изменения страницы."); return; }
    setPromoBusy(true);
    try {
      const { data } = await api.post(
        "/professional-map/complimentary/cards/" + encodeURIComponent(active.id) + "/submit",
        { expected_revision: active.revision, consent_to_publication: true }
      );
      setCards((old) => [data, ...old.filter((item) => item.id !== data.id)]);
      setActive(data);
      setForm(editable(data));
      setPublicationConsent(false);
      const invitation = await api.get("/professional-map/complimentary/mine");
      setPromoInvitation(invitation.data);
      toast.success("Страница отправлена на проверку. Публично она пока недоступна.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setPromoBusy(false);
    }
  }

  async function withdrawPromoPage() {
    if (!active || promoBusy || promoInvitation?.card_id !== active.id) return;
    if (!window.confirm("Снять страницу с публикации или проверки? Страница станет личным черновиком.")) return;
    setPromoBusy(true);
    try {
      const { data } = await api.post(
        "/professional-map/complimentary/cards/" + encodeURIComponent(active.id) + "/withdraw",
        { expected_revision: active.revision }
      );
      setCards((old) => [data, ...old.filter((item) => item.id !== data.id)]);
      setActive(data);
      setForm(editable(data));
      setPublicationConsent(false);
      const invitation = await api.get("/professional-map/complimentary/mine");
      setPromoInvitation(invitation.data);
      toast.success("Страница возвращена в черновики.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setPromoBusy(false);
    }
  }
  const labelInput = (label, name, type = "text", placeholder = "") => (
    <label className="block space-y-1.5" key={name}>
      <span className="text-xs text-[#755F4E]">{label}</span>
      <input className={field} type={type} value={form[name]} onChange={(event) => set(name, event.target.value)} placeholder={placeholder} />
    </label>
  );
  const portraitId = !portraitRemoved && active?.portrait_media_id;
  const cropPosition = { x: Number(form.portrait_crop_x ?? 50), y: Number(form.portrait_crop_y ?? 50) };
  const works = (portfolioEditing ? portfolioOrder : (active?.portfolio_media_ids || [])).filter((id) => !removedWorks.includes(id));
  const hasWork = works.length > 0 || pendingWorks.length > 0;
  const location = [form.city.trim(), form.country.trim()].filter(Boolean).join(", ");
  const displayedLinks = form.social_links.filter((link) => link.visible !== false && safeSocialUrl(link.url));
  const hasLegacyWebsite = Boolean(form.public_website && safeSocialUrl(form.public_website));
  // Profession is an explicit search attribute, never hide it because description happens to mention the word.
  const visibleSpecializations = commaList(form.specializations);
  const selectedRoles = commaList(form.specializations);
  const isRoleSelected = (role) => selectedRoles.some(
    (item) => item.toLocaleLowerCase("ru-RU") === role.toLocaleLowerCase("ru-RU")
  );

  // Same presentation as visitors see, but sourced from owner-only draft data.
  // Explicit opt-ins are applied before the data reaches the shared visual layer.
  const privateWorkIds = [...works, ...pendingWorks.map((_, index) => "pending-work-" + index)];
  const ownerProfileView = {
    public_name: form.public_name || "Имя или название",
    entity_type: form.entity_type,
    description: form.description,
    specializations: commaList(form.specializations),
    services: commaList(form.services),
    professional_qa: form.professional_qa || [],
    city: form.city, country: form.country,
    portrait_media_id: pendingPortrait ? "pending-portrait" : portraitId,
    portrait_crop_x: form.portrait_crop_x,
    portrait_crop_y: form.portrait_crop_y,
    portfolio_media_ids: privateWorkIds,
    portfolio_captions: { ...portfolioCaptions, ...Object.fromEntries(
      pendingWorks.map((work, index) => ["pending-work-" + index, work.caption])
    ) },
    public_email: form.public_contact_email_visible === true ? form.public_email : null,
    public_phone: form.public_contact_phone_visible === true ? form.public_phone : "",
    public_website: form.public_website,
    social_links: form.social_links.filter((link) => link.visible === true && safeSocialUrl(link.url)),
  };
  return (
    <div className="min-h-screen bg-[#F7F0E5] text-[#3B2C22]" data-testid="professional-card-workspace">
      <main className="mx-auto max-w-[1040px] px-4 pb-12 pt-5 sm:px-9">
        <button type="button" onClick={() => nav(returnToOrigin)} className="mb-5 inline-flex min-h-[44px] items-center gap-2 text-sm text-[#665344]" data-testid="professional-card-back-to-origin">
          <ArrowLeft className="h-4 w-4" aria-hidden="true" /> {fromAccount ? "Мой кабинет" : "К Профессиональной карте"}
        </button>
        <header className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl">Моя профессиональная страница</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-[#826751]/20 px-3 py-1 text-xs text-[#806B5C]">{active?.status === "published" ? "Опубликовано" : "Черновик"}</span>
            {canOwnerQa && <button type="button" className={softButton}
              data-testid="pm-owner-search-qa-link"
              onClick={() => nav("/account/professional-cards/search-qa")}>Посмотреть как посетитель</button>}
            {preview && active && active.status !== "published" && active.moderation_state !== "pending" && <button type="button" onClick={() => setPreview(false)} className={softButton} data-testid="pm-profile-edit"><Pencil size={16} aria-hidden="true"/> Редактировать страницу</button>}
          </div>
        </header>
        {!loading && !loadError && canStaffSelfPublish && active?.entity_type === "person" && preview && (
          <section className="mb-4 rounded-2xl border border-[#826751]/20 bg-white/80 p-4" data-testid="pm-staff-publication">
            {active.status === "published" ? (
              <div className="flex flex-wrap items-center gap-3">
                <button type="button" className={softButton} onClick={() => nav("/professional-map/profiles/" + encodeURIComponent(active.id))}
                  data-testid="pm-staff-open-public-profile">Открыть опубликованную страницу</button>
                <button type="button" className={softButton} disabled={staffPublicationBusy} onClick={staffWithdrawOwnPage}
                  data-testid="pm-staff-withdraw">Снять с публикации для редактирования</button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-[#705C4D]">Размещение собственной профессиональной страницы для Owner и Administrator бесплатное. Публикация выполняется только после Вашего подтверждения.</p>
                <label className="flex items-start gap-2 text-sm text-[#4A382B]">
                  <input type="checkbox" className="mt-1" checked={publicationConsent}
                    onChange={(e) => setPublicationConsent(e.target.checked)} data-testid="pm-staff-publish-consent" />
                  <span>Согласна на публичный показ имени, города, специализаций, описания, выбранных фотографий и видимых социальных ссылок. Email и телефон будут показаны только при отдельных включённых настройках.</span>
                </label>
                <button type="button" onClick={staffPublishOwnPage}
                  disabled={!publicationConsent || staffPublicationBusy || hasUnsavedDraft || offerEditorOpen}
                  className={softButton + " bg-[#F4EADD] disabled:opacity-50"}
                  data-testid="pm-staff-publish">{staffPublicationBusy ? "Публикуем…" : "Опубликовать мою страницу"}</button>
              </div>
            )}
          </section>
        )}
        {!loading && !loadError && !canStaffSelfPublish && promoInvitation?.state !== "none" &&
          active?.entity_type === "person" && preview &&
          (!promoInvitation?.card_id || promoInvitation.card_id === active.id) && (
          <section className="mb-4 space-y-3 rounded-2xl border border-[#826751]/20 bg-white/80 p-4"
            data-testid="pm-promo-participant">
            <h2 className="font-serif text-xl">Бесплатное размещение в Профессиональной карте</h2>
            {active.status === "published" ? (
              <>
                <p className="text-sm text-[#705C4D]" role="status">
                  {promoInvitation?.state === "active"
                    ? "Страница опубликована. Размещение действует до " +
                      (promoInvitation.expires_at ? new Date(promoInvitation.expires_at).toLocaleDateString("ru-RU") : "окончания срока") + "."
                    : "Бесплатное размещение завершено. Страница сохранена в Вашем кабинете."}
                </p>
                {promoInvitation?.state === "active" && <button type="button" className={softButton}
                  onClick={() => nav("/professional-map/profiles/" + encodeURIComponent(active.id))}>
                  Открыть опубликованную страницу
                </button>}
                <button type="button" disabled={promoBusy} className={softButton}
                  onClick={withdrawPromoPage} data-testid="pm-promo-withdraw">
                  Снять с публикации для редактирования
                </button>
              </>
            ) : active.moderation_state === "pending" ? (
              <>
                <p className="text-sm text-[#705C4D]" role="status">
                  Страница ожидает проверки. Отсчёт бесплатного срока ещё не начался.
                </p>
                <button type="button" disabled={promoBusy} className={softButton}
                  onClick={withdrawPromoPage} data-testid="pm-promo-withdraw">
                  Вернуть в черновик
                </button>
              </>
            ) : promoInvitation?.state === "issued" ? (
              <>
                <p className="text-sm text-[#705C4D]">
                  Для Вас подготовлено бесплатное размещение на {promoInvitation.duration_days} дней.
                  Срок начнётся после одобрения и публикации страницы.
                </p>
                {active.complimentary_rejection_note && <p className="text-sm text-[#9A4938]" role="status">
                  Замечание к предыдущей заявке: {active.complimentary_rejection_note}
                </p>}
                <label className="flex items-start gap-2 text-sm text-[#4A382B]">
                  <input type="checkbox" className="mt-1" checked={publicationConsent}
                    onChange={(event) => setPublicationConsent(event.target.checked)}
                    data-testid="pm-promo-consent"/>
                  <span>Согласна на публикацию имени, страны, города, специализаций, описания,
                    выбранных фотографий и видимых социальных ссылок. Почта и телефон
                    отображаются только с отдельного согласия.</span>
                </label>
                <button type="button" onClick={submitPromoPage}
                  disabled={!publicationConsent || promoBusy || hasUnsavedDraft || offerEditorOpen}
                  className={softButton + " bg-[#F4EADD] disabled:opacity-50"}
                  data-testid="pm-promo-submit">
                  {promoBusy ? "Отправляем…" : "Отправить страницу на проверку"}
                </button>
              </>
            ) : (
              <p className="text-sm text-[#705C4D]" role="status">
                Размещение сейчас неактивно. Сохранённая страница остаётся доступной Вам.
              </p>
            )}
          </section>
        )}
        {loading ? <p role="status">Загружаем страницу…</p> :
          loadError ? <div role="status" className="rounded-2xl border border-[#826751]/20 bg-white p-6">
            Не удалось загрузить сохранённую страницу. Данные не менялись.
            <button type="button" className={softButton + " ml-3"} onClick={() => window.location.reload()}>Повторить</button>
          </div> :
          <div className={preview ? "space-y-2" : "space-y-4"}>
            {(cards.length > 1 || (cards.length > 0 && !active)) && (
              <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Сохранённые страницы">
                <span className="mr-1 text-xs text-[#755F4E]">Сохранённые страницы</span>
                {cards.map((card) => (
                  <button key={card.id} type="button"
                    aria-pressed={active?.id === card.id}
                    onClick={() => switchTo(card)}
                    className={softButton + (active?.id === card.id ? " border-[#86634B] bg-[#F4EADD]" : "")}>
                    {card.public_name}
                  </button>
                ))}
              </div>
            )}
            {cards.length > 0 && active && !preview && (
              <div className="flex justify-end">
                <button type="button" onClick={createBusinessDraft} className={softButton}
                  data-testid="pm-business-draft-create"><Plus size={16} aria-hidden="true"/> Добавить компанию</button>
              </div>
            )}
            {preview && <ProfessionalProfileView
              key={active?.id || "new-draft"}
              profile={ownerProfileView}
              ownerPreview
              onEditFaq={() => { setPreview(false); setOpened((old) => ({ ...old, qa: true })); }}
              reviews={<section className="mt-5 scroll-mt-5 border-t border-[#826751]/15 pt-4"
                aria-label="Отзывы" data-testid="pm-owner-reviews-preview">
                <h2 className="font-serif text-xl">Отзывы</h2>
                <p className="mt-2 text-sm text-[#806B5C]">Отзывы посетителей будут доступны после платного размещения и публикации страницы.</p>
              </section>}
              contactActions={<div className="grid gap-2" data-testid="pm-contact-actions">
                <button type="button" onClick={() => setPreviewContactMode("contact")} className="min-h-11 rounded-xl border border-[#826751]/25 px-4 text-sm">
                  Связаться
                </button>
                <button type="button" onClick={() => setPreviewContactMode("quote")} className="min-h-11 rounded-xl border border-[#826751]/25 bg-[#F4EADD] px-4 text-sm font-medium text-[#4A382B] transition-colors hover:bg-[#EADCC9] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#755F4E]">
                  Запросить предложение
                </button>
                <p className="text-xs text-[#806B5C]">Предпросмотр. Обращения станут доступны после публикации.</p>
              </div>}
              renderPortrait={(id, profile) => id === "pending-portrait" && pendingPortrait
                ? <img src={pendingPortrait.url} alt="Портрет" className="aspect-[5/6] w-full object-cover"
                    style={{objectPosition: cropPosition.x + "% " + cropPosition.y + "%"}}/>
                : <OwnedImage mediaId={id} aspect="aspect-[5/6]" portrait position={cropPosition}/>}
              renderWork={(id, index, caption) => id.startsWith("pending-work-")
                ? <button type="button" className="block w-full overflow-hidden rounded-xl"
                    onClick={() => setLightbox({src: pendingWorks[Number(id.slice("pending-work-".length))]?.url, alt: caption || "Работа"})}>
                    <img src={pendingWorks[Number(id.slice("pending-work-".length))]?.url} alt={caption || "Работа"} className="aspect-[4/5] w-full object-cover"/>
                  </button>
                : <OwnedImage mediaId={id} onOpen={setLightbox} captionOverride={caption} showCaption={false}/>}
              offers={active ? <ProfessionalMapOffersWorkspace cardId={active.id} readOnly/> : null}
            />}
            {!preview && <div className="space-y-4" data-testid="pm-profile-composition">
            <section className={preview ? "px-4 pb-4 pt-5 sm:px-7 sm:pb-5 sm:pt-7" : "rounded-[26px] border border-[#826751]/20 bg-white/75 p-4 shadow-[0_8px_25px_rgba(66,48,36,0.04)] sm:p-7"} data-testid="pm-profile-identity">
              <div className="flex items-start gap-4 sm:gap-6">
                <div className="w-[105px] shrink-0 sm:w-[126px]">
                  {pendingPortrait ? (
                    <div className="relative">
                      <img src={pendingPortrait.url} alt="Выбранный портрет" className="aspect-[5/6] w-full rounded-2xl object-cover" style={{objectPosition: cropPosition.x + "% " + cropPosition.y + "%"}} />
                      {!preview && <div className="mt-2 flex flex-wrap gap-2"><button type="button" onClick={() => setCropSource(pendingPortrait.url)} className="text-xs underline"><Pencil size={12} className="mr-1 inline"/>Редактировать фото</button><button type="button" onClick={() => removeTemp(pendingPortrait, true)} className="text-xs underline">Убрать</button></div>}
                    </div>
                  ) : portraitId ? <OwnedImage mediaId={portraitId} aspect="aspect-[5/6]" portrait position={cropPosition}
                    onEdit={!preview ? (src) => setCropSource(src) : null}
                    onRemove={!preview ? () => setPortraitRemoved(true) : null} /> :
                    <div className="flex aspect-[5/6] items-center justify-center rounded-2xl bg-[#EFE3D1] text-[#816B56]"><Camera size={28}/></div>}
                  {!preview && <>
                    <input ref={portraitRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={selectPortrait} className="sr-only" aria-label="Выбрать портрет" />
                    <button type="button" onClick={() => portraitRef.current?.click()} className="mt-2 min-h-[40px] w-full rounded-lg border border-[#826751]/20 bg-white px-2 text-xs" data-testid="pm-profile-portrait-add">Фото профиля</button>
                  </>}
                </div>
                <div className="min-w-0 flex-1 space-y-3">
                  {preview ? <>
                    <h2 className="font-serif text-2xl sm:text-3xl">{form.public_name || "Имя или бренд"}</h2>
                    {form.entity_type === "business" && form.business_type && <p className="text-sm text-[#806B5C]">{businessLabel(form.business_type)}</p>}
                    <p className="text-sm text-[#755F4E]">{location}</p>
                    {visibleSpecializations.length > 0 && <p className="text-sm font-medium text-[#5A4536]" data-testid="pm-profile-role-display">{visibleSpecializations.join(" · ")}</p>}
                    <p className="whitespace-pre-wrap text-sm leading-6">{form.description}</p>
                  </> : <>
                    <label className="block space-y-1">
                      <span className="text-xs text-[#755F4E]">Имя или название</span>
                      <input className={field + " font-serif text-xl sm:text-2xl"} value={form.public_name} onChange={(event) => set("public_name", event.target.value)} placeholder="Имя или название бренда" data-testid="pm-profile-name" maxLength={120}/>
                    </label>
                    <label className="block space-y-1">
                      <span className="text-xs text-[#755F4E]">Чем занимаешься</span>
                      <textarea className={field} rows={3} value={form.description} onChange={(event) => set("description", event.target.value)} maxLength={3000} placeholder="Например: авторские десерты, обучение, торты на заказ" />
                    </label>
                  </>}

                </div>
              </div>
              {preview && ((form.public_contact_email_visible && form.public_email) || (form.public_contact_phone_visible && form.public_phone) || hasLegacyWebsite || displayedLinks.length > 0) && (              <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-[#826751]/15 pt-4" data-testid="pm-profile-header-contacts">
                {form.public_contact_email_visible && form.public_email && <a href={"mailto:" + form.public_email} className="break-all text-sm underline">{form.public_email}</a>}
                {form.public_contact_phone_visible && form.public_phone && <a href={"tel:" + form.public_phone.replace(/[^+\d]/g, "")} className="text-sm underline">{form.public_phone}</a>}
                {hasLegacyWebsite && <a href={form.public_website} target="_blank" rel="noopener noreferrer" className="text-sm underline">Сайт</a>}
                {displayedLinks.map((link, i) => <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="text-sm underline">{socialLabel(link.platform)}</a>)}
              </div>)}
              {!preview && <ProfessionalMapGeographyFields
                country={form.country} region={form.region} city={form.city}
                onChange={(fields) => setForm((current) => ({ ...current, ...fields }))} />}
              {!preview && (
                <div className="mt-5 border-t border-[#826751]/15 pt-4">
                  {!active && <div className="grid gap-2 sm:grid-cols-2" role="group" aria-label="Формат страницы" data-testid="pm-profile-type">
                    {[{ value: "person", title: "Личная страница" }, { value: "business", title: "Страница компании" }].map((choice) =>
                      <button key={choice.value} type="button"
                        aria-pressed={form.entity_type === choice.value}
                        onClick={() => { set("entity_type", choice.value); setShowRoleChoices(false); setShowBusinessChoice(false); }}
                        className={softButton + (form.entity_type === choice.value ? " border-[#86634B] bg-[#F4EADD]" : "")}>
                        {form.entity_type === choice.value && <Check size={15} aria-hidden="true"/>}{choice.title}
                      </button>)}
                  </div>}
                  {form.entity_type === "person" && <div className={!active ? "mt-4" : ""} data-testid="pm-role-specializations">
                    <p className="mb-2 text-xs text-[#755F4E]">Специализация</p>
                    <button type="button" className={softButton + " max-w-full justify-between"}
                      data-testid="pm-role-choose" aria-expanded={showRoleChoices}
                      onClick={() => setShowRoleChoices((current) => !current)}>
                      <span className="min-w-0 truncate text-left">
                        {selectedRoles.length ? selectedRoles.join(" · ") : "Выбрать специализацию"}
                      </span>
                      <ChevronDown size={16} className={"shrink-0 transition-transform " + (showRoleChoices ? "rotate-180" : "")} aria-hidden="true"/>
                    </button>
                    {showRoleChoices && <div className="mt-3 max-h-[240px] space-y-3 overflow-y-auto rounded-xl border border-[#826751]/20 bg-[#FFFCF8] p-3"
                      role="group" aria-label="Профессии и специализации" data-testid="pm-role-options">
                      {PROFESSIONAL_ROLE_GROUPS.map((group) =>
                        <div key={group.title}>
                          <p className="mb-2 text-xs font-medium text-[#806B5C]">{group.title}</p>
                          <div className="flex flex-wrap gap-2">
                            {group.roles.map((role) => <button type="button" key={role}
                              className={softButton + (isRoleSelected(role) ? " border-[#86634B] bg-[#F4EADD]" : "")}
                              aria-pressed={isRoleSelected(role)} onClick={() => toggleRole(role)}>
                              {isRoleSelected(role) && <Check size={15} aria-hidden="true"/>}{role}
                            </button>)}
                          </div>
                        </div>)}
                      <div className="flex justify-end">
                        <button type="button" className={softButton} onClick={() => setShowRoleChoices(false)}
                          data-testid="pm-role-done">Готово</button>
                      </div>
                    </div>}
                  </div>}
                  {form.entity_type === "business" && <div className="mt-4" data-testid="pm-business-type-field">
                    <p className="mb-2 text-xs text-[#755F4E]">Вид бизнеса</p>
                    <button type="button" className={softButton} onClick={() => setShowBusinessChoice((v) => !v)}
                      aria-expanded={showBusinessChoice || !form.business_type} data-testid="pm-business-choose">
                      {businessLabel(form.business_type) || "Выбрать вид бизнеса"} <ChevronDown size={16} className={"transition-transform " + (showBusinessChoice ? "rotate-180" : "")} aria-hidden="true"/>
                    </button>
                    {(showBusinessChoice || !form.business_type) && <div className="mt-3 grid max-h-[240px] gap-2 overflow-y-auto rounded-xl border border-[#826751]/20 bg-[#FFFCF8] p-3 sm:grid-cols-2" role="group" aria-label="Вид бизнеса">
                      {BUSINESS_TYPES.map(([value, label]) => <button key={value} type="button"
                        onClick={() => { set("business_type", value); setShowBusinessChoice(false); }}
                        aria-pressed={form.business_type === value}
                        className={softButton + " justify-start text-left " + (form.business_type === value ? " border-[#86634B] bg-[#F4EADD]" : "")}>
                        {form.business_type === value && <Check size={16} aria-hidden="true"/>}{label}
                      </button>)}
                    </div>}
                  </div>}
                </div>
              )}
            {!preview && <>
              <div className="mt-4 border-t border-[#826751]/15 pt-4" data-testid="pm-profile-contacts-section">
                <button type="button" onClick={() => setShowContacts((v) => !v)} aria-expanded={showContacts} className="flex w-full min-h-11 items-center justify-between font-serif text-xl">
                  Контакты и соцсети {showContacts ? <ChevronDown size={18}/> : <Plus size={18}/>}
                </button>
                {showContacts && <div className="mt-4 space-y-4">
                  <p className="text-xs leading-5 text-[#806B5C]">Телефон и email скрыты от посетителей по умолчанию. Ты сам выбираешь, что показать публично.</p>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      {labelInput("Электронная почта", "public_email", "email")}
                      <label className="mt-2 flex min-h-10 items-center gap-2 text-sm text-[#614E40]">
                        <input type="checkbox" data-testid="pm-contact-show-email"
                          checked={Boolean(form.public_contact_email_visible) && Boolean(form.public_email.trim())}
                          disabled={!form.public_email.trim()}
                          onChange={(event) => set("public_contact_email_visible", event.target.checked)} />
                        Показывать email на публичной странице
                      </label>
                    </div>
                    <div>
                      {labelInput("Номер телефона", "public_phone", "tel")}
                      <label className="mt-2 flex min-h-10 items-center gap-2 text-sm text-[#614E40]">
                        <input type="checkbox" data-testid="pm-contact-show-phone"
                          checked={Boolean(form.public_contact_phone_visible) && Boolean(form.public_phone.trim())}
                          disabled={!form.public_phone.trim()}
                          onChange={(event) => set("public_contact_phone_visible", event.target.checked)} />
                        Показывать телефон на публичной странице
                      </label>
                    </div>
                  </div>
                  {form.public_website && <label className="block text-sm text-[#755F4E]">Сайт (ранее добавленный)
                    <input type="url" className={field + " mt-1"} value={form.public_website}
                      onChange={(e) => set("public_website", e.target.value)} aria-label="Ранее добавленный сайт"/>
                  </label>}
                  {form.social_links.map((link, i) => <div key={i} className="rounded-xl border border-[#826751]/15 bg-[#FFFCF8] p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="text-sm font-medium">{socialLabel(link.platform)}</span>
                      <button type="button" className={softButton} aria-label={"Удалить ссылку " + (i+1)}
                        onClick={() => set("social_links",form.social_links.filter((_,n)=>n!==i))}><Trash2 size={15}/></button>
                    </div>
                    <input type="url" className={field + " mt-2"} placeholder="https://" aria-label={"Ссылка " + (i+1)} value={link.url}
                      onChange={(e) => set("social_links",form.social_links.map((v,n)=>n===i?{...v,url:e.target.value}:v))}/>
                    <label className="mt-2 flex min-h-10 items-center gap-2 text-sm">
                      <input type="checkbox" checked={link.visible !== false}
                        onChange={(e) => set("social_links",form.social_links.map((v,n)=>n===i?{...v,visible:e.target.checked}:v))}/>
                      Показывать на странице
                    </label>
                  </div>)}
                  <button type="button" className={softButton} data-testid="pm-profile-social-add"
                    onClick={() => setShowSocialChoice((v) => !v)} aria-expanded={showSocialChoice}>
                    <Plus size={16}/> Добавить соцсеть или сайт
                  </button>
                  {showSocialChoice && <div role="group" aria-label="Выбрать соцсеть" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {SOCIAL_TYPES.filter(([id]) => id==="other" || !form.social_links.some((l)=>l.platform===id)).map(([id,label])=>
                      <button type="button" key={id} className={softButton} onClick={()=>{
                        set("social_links",[...form.social_links,{platform:id,url:"",visible:true}]); setShowSocialChoice(false);
                      }}>{label}</button>)}
                  </div>}
                </div>}
              </div>
            </>}
            </section>
            {(!preview || (form.professional_qa || []).length > 0) && (
              <Section title="Вопросы и ответы" hint="" open={preview || !!opened.qa}
                onToggle={() => !preview && toggle("qa")} testId="pm-profile-own-qa" hideToggle={preview} joined={preview}>
                {!preview && <p className="mb-3 text-sm text-[#806B5C]">Добавь вопросы, которые тебе часто задают клиенты, и свои ответы на них. Посетители смогут прочитать их на твоей профессиональной странице.</p>}
                <div className="space-y-3">
                  {(form.professional_qa || []).map((item, index) => <div key={index}
                    className="rounded-xl border border-[#826751]/15 bg-[#FFFCF8] p-3">
                    {preview ? <details className="group" data-testid={"pm-own-qa-preview-" + index}>
                      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-2 text-sm font-medium">
                        {item.question}<ChevronDown className="h-4 w-4 shrink-0 group-open:rotate-180" aria-hidden="true"/>
                      </summary>
                      <p className="whitespace-pre-wrap pt-2 text-sm leading-6">{item.answer}</p>
                    </details> : <>
                      <label className="block text-xs text-[#755F4E]">Вопрос
                        <input className={field + " mt-1"} value={item.question} maxLength={240}
                          onChange={(e) => set("professional_qa",form.professional_qa.map((q,n) => n === index ? {...q,question:e.target.value} : q))}
                          placeholder="Например: что вдохновляет тебя в работе?"/>
                      </label>
                      <label className="mt-2 block text-xs text-[#755F4E]">Ответ
                        <textarea className={field + " mt-1"} rows={3} value={item.answer} maxLength={1500}
                          onChange={(e) => set("professional_qa",form.professional_qa.map((q,n) => n === index ? {...q,answer:e.target.value} : q))}/>
                      </label>
                      <button type="button" className={softButton + " mt-2"} aria-label={"Удалить вопрос " + (index+1)}
                        onClick={() => set("professional_qa",form.professional_qa.filter((_,n) => n !== index))}>
                        <Trash2 size={14}/> Удалить вопрос
                      </button>
                    </>}
                  </div>)}
                </div>
                {!preview && <button type="button" className={softButton + " mt-3"}
                  onClick={() => set("professional_qa",[...(form.professional_qa || []),{question:"",answer:""}])}
                  data-testid="pm-add-own-qa" disabled={(form.professional_qa || []).length >= 50}>
                  <Plus size={16}/> Добавить вопрос и ответ
                </button>}
              </Section>
            )}
            {(!preview || hasWork) && <Section title="Фотографии" hint={hasWork ? "" : "Фотографии в формате 4:5"} open={preview || !!opened.works} onToggle={() => !preview && toggle("works")} testId="pm-profile-works-section" hideToggle={preview} joined={preview}>
              {!preview && <>
                <input ref={worksRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={selectWorks} className="sr-only" aria-label="Выбрать фотографии работ"/>
                <button type="button" className={softButton} onClick={() => worksRef.current?.click()} data-testid="pm-profile-work-add">
                  <ImagePlus size={18}/> Добавить фотографии
                </button>
              </>}
              {!preview && active && <>
                <input type="file" ref={quickWorkRef} onChange={quickAddWork} accept="image/jpeg,image/png,image/webp"
                  className="sr-only" aria-label="Быстро добавить фотографию работы" />
                <button type="button" className={softButton + " mb-3"} disabled={quickUploading}
                  onClick={() => quickWorkRef.current?.click()} data-testid="pm-work-quick-add">
                  <Plus size={16} aria-hidden="true" /> {quickUploading ? "Добавляем…" : "Добавить работу"}
                </button>
              </>}
              {!preview && active && (works.length > 0 || portfolioEditing) && (
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  {!portfolioEditing ? <button type="button" onClick={beginPortfolioEditing}
                    className={softButton} data-testid="pm-work-manage">
                    <Pencil size={15} aria-hidden="true" /> Изменить подписи и порядок
                  </button> : <>
                    <button type="button" disabled={portfolioSaving} onClick={savePortfolioEditing}
                      className={softButton + " bg-[#F4EADD]"} data-testid="pm-work-manage-save">
                      {portfolioSaving ? "Сохраняем…" : "Сохранить работы"}
                    </button>
                    <button type="button" disabled={portfolioSaving} className={softButton}
                      data-testid="pm-work-manage-cancel" onClick={() => setPortfolioEditing(false)}>
                      Отменить
                    </button>
                  </>}
                </div>
              )}
              {hasWork ? <div className="grid grid-cols-3 gap-2 sm:gap-3" data-testid="professional-portfolio-gallery">
                {works.map((id, index) => <div key={id} className="min-w-0">
                  <OwnedImage mediaId={id} onOpen={setLightbox}
                    captionOverride={portfolioEditing || !preview ? portfolioCaptions[id] : active?.portfolio_captions?.[id]}
                    onRemove={!preview ? () => setRemovedWorks((old) => [...old, id]) : null}/> 
                  {!preview && <label className="mt-2 block space-y-1" data-testid={"pm-existing-work-caption-" + id}>
                    <span className="text-xs text-[#755F4E]">Подпись к фотографии</span>
                    <input type="text" className={field + " !px-2 !py-2"} maxLength={200}
                      aria-label={"Подпись к сохранённой работе " + (index + 1)}
                      placeholder="Что изображено на фотографии?"
                      value={portfolioCaptions[id] ?? ""}
                      onChange={(event) => setPortfolioCaptions((old) => ({ ...old, [id]: event.target.value }))} />
                  </label>}
                  {portfolioEditing && <div className="mt-2 space-y-2" data-testid={"pm-work-tools-" + id}>
                    <input type="text" maxLength={200} className={field + " !px-2 !py-2"}
                      aria-label={"Подпись к работе " + (index + 1)}
                      placeholder="Название работы"
                      value={portfolioCaptions[id] || ""}
                      onChange={(event) => setPortfolioCaptions((old) => ({ ...old, [id]: event.target.value }))}/>
                    <div className="flex flex-wrap gap-1">
                      <button type="button" className={softButton + " !min-h-9 !px-2"} disabled={index === 0}
                        aria-label={"Переместить работу " + (index+1) + " влево"} onClick={() => moveWork(id, -1)}>←</button>
                      <button type="button" className={softButton + " !min-h-9 !px-2"} disabled={index === works.length - 1}
                        aria-label={"Переместить работу " + (index+1) + " вправо"} onClick={() => moveWork(id, 1)}>→</button>
                      <button type="button" className={softButton + " !min-h-9 !px-2"} aria-label={"Убрать работу " + (index+1) + " из портфолио"}
                        onClick={() => setPortfolioOrder((old) => old.filter((item) => item !== id))}><Trash2 size={14}/></button>
                    </div>
                  </div>}
                </div>)}
                {pendingWorks.map((item, i) => <figure key={item.url}>
                  <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-[#F2E9DC]">
                    <button type="button" onClick={() => setLightbox({ src: item.url, alt: item.caption || "Новая работа" })}
                      className="h-full w-full cursor-zoom-in" aria-label={"Увеличить фотографию " + (item.caption || "работы")}>
                      <img src={item.url} alt={item.caption || "Новая работа"} className="h-full w-full object-cover" />
                    </button>
                    {!preview && <button type="button" onClick={() => removeTemp(item)} className="absolute right-2 top-2 flex h-10 w-10 items-center justify-center rounded-xl bg-white/95" aria-label="Убрать выбранную работу"><Trash2 size={16}/></button>}
                  </div>
                  {!preview ? <input className={field + " mt-2"} value={item.caption} maxLength={200}
                    aria-label={"Подпись к работе " + (i + 1)} onChange={(e) => setPendingWorks((old) => old.map((p) => p === item ? { ...p, caption: e.target.value } : p))} placeholder="Подпись к работе" /> :
                    <figcaption className="mt-2 text-xs">{item.caption}</figcaption>}
                </figure>)}
              </div> : <p className="text-sm text-[#806B5C]">Добавь первую фотографию — она появится здесь ещё до сохранения.</p>}
            </Section>}
            {active && <ProfessionalMapOffersWorkspace cardId={active.id} readOnly={false} onEditingChange={setOfferEditorOpen}/>}
            </div>}
            {!preview && <div className="flex flex-wrap gap-2 rounded-2xl border border-[#826751]/15 bg-[#FFFCF8]/95 p-3">
              <button type="button" onClick={returnToProfile} className={softButton} data-testid="professional-card-show-preview"><Eye size={17}/> Вернуться к странице</button>
              <button type="button" disabled={saving} onClick={save} className={softButton + " bg-[#F4EADD] font-medium disabled:opacity-50"} data-testid="professional-card-save">
                <Save size={17}/> {saving ? "Сохранение…" : "Сохранить и вернуться"}
              </button>
            </div>}
          </div>
        }
        {!loading && !loadError && <ProfessionalMapInbox />}
        {previewContactMode && <ProfessionalMapContactDialog
          cardId={active?.id || ""} profile={ownerProfileView} mode={previewContactMode} previewOnly
          onClose={() => setPreviewContactMode(null)} />}
        {lightbox && <div role="dialog" aria-modal="true" aria-label="Просмотр фотографии работы"
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/85 px-3 py-8"
          onClick={() => setLightbox(null)} data-testid="pm-work-lightbox">
          <button type="button" className="absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-[#3A2B22]"
            aria-label="Закрыть фотографию" onClick={() => setLightbox(null)}><X size={20} /></button>
          <img src={lightbox.src} alt={lightbox.alt || "Работа"} onClick={(e) => e.stopPropagation()}
            className="max-h-[82dvh] max-w-full rounded-xl object-contain" />
        </div>}
        {cropSource && <PortraitCropEditor src={cropSource} position={cropPosition}
          onClose={() => setCropSource(null)}
          onApply={({x,y}) => { set("portrait_crop_x",x); set("portrait_crop_y",y); setCropSource(null); }} />}
      </main>
    </div>
  );
}
